// Core packages
import { CollectionViewer } from '@angular/cdk/collections';
import { DataSource } from '@angular/cdk/table';

// Third party packages
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

// Custom packages
import { PrivacyDocsInterface } from 'src/app/shared/models/privacy-docs/privacy-docs.interface';
import { HelperService } from 'src/app/shared/services/helper.service';
import { PrivacyDocsService } from 'src/app/shared/services/privacy-docs.service';

/**
 * @see https://blog.angular-university.io/angular-material-data-table/
 *
 * NB
 * If you need to change this to make a new DataSource
 * just change the following:
 *
 * - Change The class name in the first line
 * - Change DataSource<T> (use your model type) in first line
 * - Change BehaviourSubject<T> in the second line
 * - Change the service inside the class constructor
 * - Change the Observable<T> used in the connect() line
 * - Change the service name inside loadItems()
 */
/**
 * Page size used to fetch the entire back-end result set when the
 * requested type filter must be applied client-side (@see allowedTypes
 * in loadDocumentsItems below): the back-end privacy-docs endpoint only
 * supports filtering by a single "type" value, so when more than one
 * type must be shown together in the same table, the whole list is
 * fetched once and then filtered/paginated in memory instead.
 */
const FETCH_ALL_LIMIT = 100;

export class PrivacyDocsDataSource implements DataSource<PrivacyDocsInterface> {
  private itemsSubject$ = new BehaviorSubject<PrivacyDocsInterface[]>([]);
  private totalCountSubject$ = new BehaviorSubject<number>(0);
  private loadingSubject$ = new BehaviorSubject<boolean>(true);

  public loading$ = this.loadingSubject$.asObservable();
  public totalCount$ = this.totalCountSubject$.asObservable();

  /**
   * Class constructor
   */
  constructor(
    private privacyDocsService: PrivacyDocsService,
    private helperService: HelperService
  ) { }

  /**
   * Connect data table to itemsSubject$
   *
   * @since 1.0.0
   *
   * @param collectionViewer collection viewer object
   * @returns Observable
   */
  connect(collectionViewer: CollectionViewer): Observable<PrivacyDocsInterface[]> {
    return this.itemsSubject$.asObservable();
  }

  /**
   * Disconnect data table from subjects
   *
   * @since 1.0.0
   *
   * @param collectionViewer collection viewer object
   */
  disconnect(collectionViewer: CollectionViewer): void {
    this.itemsSubject$.complete();
    this.totalCountSubject$.complete();
    this.loadingSubject$.complete();
  }

  /**
   * Load items from back-end using service injected in the constructor
   *
   * @since 1.0.0
   *
   * @param companyId Company Id
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering objects with filtering data
   * @param [allowedTypes] When set, only documents whose "type" is in this list are kept;
   *                       since the back-end only filters by a single type value, the full
   *                       result set is fetched and filtering/pagination happen client-side
   */
  loadDocumentsItems(
    companyId: string,
    start?: number,
    limit?: number,
    sort?: string,
    sortVersus?: string,
    search?: any,
    allowedTypes?: string[]
  ): void {
    this.loadingSubject$.next(true); // Start loading

    if (allowedTypes && allowedTypes.length) {
      this.privacyDocsService.getList(companyId, 0, FETCH_ALL_LIMIT, sort, sortVersus, search)
        .pipe(
          catchError(err => { // If errors, return empty array
            this.helperService.handleError(err);
            return of({ status: true, data: { items: [], totalCount: 0 } });
          }),
          finalize(() => this.loadingSubject$.next(false)) // Stop loading on finalize
        )
        .subscribe((result: any) => {
          const filteredItems = ((result.data.items || []) as PrivacyDocsInterface[])
            .filter(item => !!item.type && allowedTypes.includes(item.type));
          const pageStart = start || 0;
          const pageLimit = limit || filteredItems.length;
          this.itemsSubject$.next(filteredItems.slice(pageStart, pageStart + pageLimit));
          this.totalCountSubject$.next(filteredItems.length);
        });
      return;
    }

    this.privacyDocsService.getList(companyId, start, limit, sort, sortVersus, search)
      .pipe(
        catchError(err => { // If errors, return empty array
          this.helperService.handleError(err);
          return of({ status: true, data: { items: [], totalCount: 0 } });
        }),
        finalize(() => this.loadingSubject$.next(false)) // Stop loading on finalize
      )
      .subscribe((result: any) => {
        this.itemsSubject$.next(result.data.items);
        this.totalCountSubject$.next(result.data.totalCount);
      });
  }

}
